// In-App colour rules exactly as both SDKs apply them (shared vectors:
// fixtures/inapp-colour-vectors.json, push-android). Colours are CSS: #RGB, #RGBA, #RRGGBB,
// #RRGGBBAA (alpha last), rgb(), rgba() (alpha 0..1), transparent and the 16 basic names.
// Results are canonical "#RRGGBBAA" upper-case; null = does not parse.

const NAMES: Record<string, string> = {
  black: "000000", silver: "C0C0C0", gray: "808080", white: "FFFFFF", maroon: "800000", red: "FF0000",
  purple: "800080", fuchsia: "FF00FF", green: "008000", lime: "00FF00", olive: "808000", yellow: "FFFF00",
  navy: "000080", blue: "0000FF", teal: "008080", aqua: "00FFFF",
};
const hx = (n: number) => n.toString(16).toUpperCase().padStart(2, "0");

export function parseColour(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const s = v.trim().toLowerCase();
  if (!s) return null;
  if (s === "transparent") return "#00000000";
  if (NAMES[s]) return `#${NAMES[s]}FF`;
  let m = /^#([0-9a-f]{3,8})$/.exec(s);
  if (m) {
    const h = m[1];
    if (h.length === 3 || h.length === 4) {
      const e = h.split("").map((c) => c + c).join("");
      return `#${(e.length === 6 ? e + "ff" : e).toUpperCase()}`;
    }
    if (h.length === 6) return `#${h.toUpperCase()}FF`;
    if (h.length === 8) return `#${h.toUpperCase()}`;
    return null;
  }
  m = /^rgba?\(([^)]*)\)$/.exec(s); // parts are trimmed below; no leading \s* (it overlaps [^)]* and backtracks)
  if (m) {
    const parts = m[1].split(",").map((p) => p.trim());
    const isA = s.startsWith("rgba");
    if (parts.length !== (isA ? 4 : 3)) return null;
    const rgb = parts.slice(0, 3).map((p) => (/^\d+$/.test(p) ? Number(p) : NaN));
    if (rgb.some((n) => !(n >= 0 && n <= 255))) return null;
    let a = 255;
    if (isA) {
      const f = /^(?:\d+(?:\.\d+)?|\.\d+)$/.test(parts[3]) ? Number(parts[3]) : NaN;
      if (!(f >= 0 && f <= 1)) return null;
      a = Math.round(f * 255);
    }
    return `#${hx(rgb[0])}${hx(rgb[1])}${hx(rgb[2])}${hx(a)}`;
  }
  return null;
}

const blank = (v: unknown) => v == null || (typeof v === "string" && !v.trim());
const cssFallback = (v: unknown): string | null => {
  // Capture everything up to ")" and trim after — a lazy capture between \s* runs backtracks cubically.
  const m = typeof v === "string" ? /^\s*var\(\s*--[^,)]+,([^)]*)\)\s*$/i.exec(v) : null;
  const fallback = m ? m[1].trim() : "";
  return fallback ? parseColour(fallback) : null;
};

// A present, non-blank value that may not parse: itself, else the brand, else var(--x, <fallback>).
export function resolvePresent(value: unknown, brand?: unknown): string | null {
  return parseColour(value) || parseColour(brand) || cssFallback(value);
}

export function resolveColour(value: unknown, dflt: string, brand?: unknown): string {
  if (blank(value)) return parseColour(dflt) as string;
  return resolvePresent(value, brand) || (parseColour(dflt) as string);
}
export const LEGACY_ACCENT = "#3B6FE0";
export const accentColour = (value: unknown, brand?: unknown): string =>
  resolveColour(value, parseColour(brand) ? (brand as string) : LEGACY_ACCENT, brand);
export const backgroundColour = (value: unknown, brand?: unknown): string => resolveColour(value, "#FFFFFF", brand);

const lum = (c: string) => {
  const f = (i: number) => { const v = parseInt(c.slice(i, i + 2), 16) / 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
  return 0.2126 * f(1) + 0.7152 * f(3) + 0.0722 * f(5);
};
export function contrast(a: string, b: string): number {
  const la = lum(a), lb = lum(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

// B-4: the label colour a button actually gets. `color` and `background` are resolved colours.
export function buttonLabelColour(style: unknown, color: string, textColor: unknown, background: string, brand?: unknown, lastResort = "#1A202C"): string {
  const st = String(style || "").toLowerCase();
  if (st !== "secondary" && st !== "outline" && st !== "ghost") return resolveColour(textColor, "#FFFFFF", brand);
  const bg = parseColour(background) || "#FFFFFFFF";
  const cands = [...(blank(textColor) ? [] : [resolveColour(textColor, color, brand)]), parseColour(color)].filter((x): x is string => !!x);
  return cands.find((c) => contrast(c, bg) >= 1.5) || (parseColour(lastResort) as string);
}
